/**
 * One row in the QC results list.
 *
 * Split into three small subcomponents so each function stays well under
 * the eslint max-lines-per-function (75) and complexity (10) budgets.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormatters } from '../../../../i18n/hooks/use-formatters'
import type { QcCheckResult } from '../../../../types/qc'
import { ResultPill } from './qc-review-panel.parts'

interface QcCheckRowProps {
  result: QcCheckResult
  onAcknowledge: (result: QcCheckResult) => void
}

const PCT_FRACTION_DIGITS = 1

interface Formatters {
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string
}

function formatParamValue(value: unknown, key: string, formatters: Formatters): string {
  if (typeof value === 'number') {
    const isPct = key.toLowerCase().includes('pct')
    return formatters.formatNumber(
      value,
      isPct
        ? { maximumFractionDigits: PCT_FRACTION_DIGITS }
        : { maximumFractionDigits: 0 }
    )
  }
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.join(', ')
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

function buildFormattedParams(
  params: Record<string, unknown>,
  formatters: Formatters
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) {
    out[key] = formatParamValue(value, key, formatters)
  }
  return out
}

function readArtefactName(detail: Record<string, unknown>): string | null {
  const value = detail.artefactName
  return typeof value === 'string' ? value : null
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

interface RowHeaderProps {
  result: QcCheckResult
  artefactName: string | null
}

function RowHeader({ result, artefactName }: RowHeaderProps): React.ReactElement {
  const { t } = useTranslation('qc')
  const titleKey = `checks.${result.checkCode.toLowerCase()}.title` as const
  return (
    <div className="flex flex-wrap items-center gap-2">
      <ResultPill status={result.status} t={t} />
      <span className="font-mono text-slate-500 dark:text-slate-500">{result.checkCode}</span>
      <span className="font-medium text-slate-800 dark:text-slate-200">
        {t(titleKey, { defaultValue: result.checkCode })}
      </span>
      {artefactName !== null && (
        <span className="text-slate-500 dark:text-slate-400">· {artefactName}</span>
      )}
      {result.acknowledgedAt !== null && (
        <span className="ml-auto text-slate-500 dark:text-slate-400">
          ✓ {t('actions.acknowledge')}
        </span>
      )}
    </div>
  )
}

interface RowActionsProps {
  result: QcCheckResult
  expanded: boolean
  onToggleExpanded: () => void
  onAcknowledge: () => void
}

function RowActions({
  result,
  expanded,
  onToggleExpanded,
  onAcknowledge,
}: RowActionsProps): React.ReactElement {
  const { t } = useTranslation('qc')
  return (
    <>
      <button
        type="button"
        onClick={onToggleExpanded}
        className="mt-2 text-[11px] text-slate-500 underline-offset-2 hover:underline dark:text-slate-400"
      >
        {expanded ? t('actions.collapseDetail') : t('actions.expandDetail')}
      </button>
      {expanded && (
        <pre className="mt-2 overflow-x-auto rounded bg-slate-50 p-2 text-[10px] text-slate-600 dark:bg-slate-900 dark:text-slate-400">
          {JSON.stringify(result.detail, null, 2)}
        </pre>
      )}
      {result.status === 'fail' && result.acknowledgedAt === null && (
        <div className="mt-2">
          <button
            type="button"
            onClick={onAcknowledge}
            className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300 dark:hover:bg-amber-950/50"
          >
            {t('actions.acknowledge')}
          </button>
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Top-level row
// ---------------------------------------------------------------------------

export function QcCheckRow({
  result,
  onAcknowledge,
}: QcCheckRowProps): React.ReactElement {
  const { t } = useTranslation('qc')
  const formatters = useFormatters()
  const [expanded, setExpanded] = useState(false)

  const formattedRecommendation = useMemo(() => {
    if (result.recommendationKey === null) return null
    const params = buildFormattedParams(result.recommendationParams, formatters)
    return t(result.recommendationKey, { ...params, defaultValue: '' })
  }, [result.recommendationKey, result.recommendationParams, formatters, t])

  const artefactName = readArtefactName(result.detail)
  const detailKey =
    `checks.${result.checkCode.toLowerCase()}.${result.status}Detail` as const
  const showText = formattedRecommendation !== null || result.status !== 'pass'

  return (
    <li className="rounded border border-slate-200 bg-white p-3 text-xs dark:border-slate-700 dark:bg-slate-800">
      <RowHeader result={result} artefactName={artefactName} />
      {showText && (
        <p className="mt-2 text-slate-700 dark:text-slate-300">
          {formattedRecommendation ?? t(detailKey, { defaultValue: '' })}
        </p>
      )}
      <RowActions
        result={result}
        expanded={expanded}
        onToggleExpanded={() => {
          setExpanded(!expanded)
        }}
        onAcknowledge={() => {
          onAcknowledge(result)
        }}
      />
    </li>
  )
}
